import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { unwrap } from "@tern/api-client";
import { Plus, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { ProjectIconPicker } from "@/components/ProjectIconPicker";
import { DEFAULT_PROJECT_COLOR } from "@/lib/projectIcons";
import { StateIcon, LabelDot } from "@/components/PropertyIcons";
import { api, errorMessage } from "@/lib/api";
import { STATE_TYPE_ORDER } from "@/lib/issues";
import { liveLabels, liveStates, restoreIssue, useProject, useProjectStore, type ProjectData } from "@/lib/store";
import { formatRelative } from "@/lib/issues";
import { toast } from "@/lib/toast";
import { ProjectLoading, useLoadProject } from "./ProjectHome";

const SECTIONS = ["general", "states", "labels", "trash"] as const;
type Section = (typeof SECTIONS)[number];

export function ProjectSettingsPage() {
  const { t } = useTranslation();
  const params = useParams({ strict: false }) as { ws: string; key: string; section?: string };
  const { ws, key } = params;
  const section: Section = SECTIONS.includes(params.section as Section) ? (params.section as Section) : "general";
  useLoadProject(ws, key);
  const d = useProject(ws, key);
  if (!d) return <ProjectLoading ws={ws} kkey={key} />;
  const isAdmin = d.project.role === "admin";
  return (
    <>
      <PageHeader crumbs={[d.project.name, t("nav.settings")]} />
      <div className="flex flex-1 overflow-hidden">
        <nav className="w-[200px] shrink-0 border-r border-border p-3">
          {SECTIONS.map((s) => (
            <Link key={s} to="/$ws/project/$key/settings/$section" params={{ ws, key, section: s }} className={`flex h-7 items-center rounded-md px-2 text-[13px] ${section === s ? "bg-active text-fg" : "text-muted hover:bg-hover hover:text-fg"}`}>
              {t(`projectSettings.${s}`)}
            </Link>
          ))}
        </nav>
        <div className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-[640px]">
            {!isAdmin && <p className="mb-4 rounded-md border border-border bg-sidebar px-3 py-2 text-[12.5px] text-muted">{t("projectSettings.readOnly")}</p>}
            {section === "general" && <General key={d.project.id} d={d} isAdmin={isAdmin} />}
            {section === "states" && <States d={d} isAdmin={isAdmin} />}
            {section === "labels" && <Labels d={d} canEdit={!!d.project.role} />}
            {section === "trash" && <TrashSection d={d} />}
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5 text-[12px] font-medium text-muted">
      {label}
      {children}
      {hint && <span className="font-normal text-faint">{hint}</span>}
    </label>
  );
}

function General({ d, isAdmin }: { d: ProjectData; isAdmin: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState(d.project.name);
  const [key, setKey] = useState(d.project.key);
  const [appearance, setAppearance] = useState({ icon: d.project.icon ?? null, color: d.project.color ?? DEFAULT_PROJECT_COLOR });
  const patch = useMutation({
    mutationFn: async (body: Record<string, unknown>) => unwrap(await api.PATCH("/api/workspaces/{slug}/projects/{key}", { params: { path: { slug: d.slug, key: d.project.key } }, body })),
    onSuccess: async (p) => {
      useProjectStore.getState().applyProject(p);
      await qc.invalidateQueries({ queryKey: ["projects", d.slug] });
      if (p.key !== d.project.key) {
        await useProjectStore.getState().load(d.slug, p.key, { force: true });
        await navigate({ to: "/$ws/project/$key/settings/$section", params: { ws: d.slug, key: p.key, section: "general" } });
      }
      toast(t("settings.saved"));
    },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const del = useMutation({
    mutationFn: async () => unwrap(await api.DELETE("/api/workspaces/{slug}/projects/{key}", { params: { path: { slug: d.slug, key: d.project.key } } })),
    onSuccess: async () => {
      useProjectStore.getState().drop(d.project.id);
      await qc.invalidateQueries({ queryKey: ["projects", d.slug] });
      await navigate({ to: "/$ws/inbox", params: { ws: d.slug } });
    },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const [confirm, setConfirm] = useState("");
  return (
    <section className="flex flex-col gap-5">
      <h2 className="text-[16px] font-semibold">{t("projectSettings.general")}</h2>
      <Field label={t("project.name")}>
        <div className="flex gap-2">
          <input className="input h-8 max-w-[320px]" value={name} disabled={!isAdmin} onChange={(e) => setName(e.target.value)} />
          <button className="btn h-8" disabled={!isAdmin || name.trim() === d.project.name} onClick={() => patch.mutate({ name: name.trim() })}>{t("common.save")}</button>
        </div>
      </Field>
      <Field label={t("project.key")} hint={t("projectSettings.keyHint")}>
        <div className="flex gap-2">
          <input className="input h-8 w-[120px] font-mono uppercase" value={key} maxLength={6} disabled={!isAdmin} onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} />
          <button className="btn h-8" disabled={!isAdmin || key === d.project.key || key.length < 2} onClick={() => patch.mutate({ key })}>{t("common.save")}</button>
        </div>
      </Field>
      <div className="flex flex-col gap-1.5">
        <span className="text-[12px] font-medium text-muted">{t("projectIcons.appearance")}</span>
        <div className="flex items-center gap-2.5">
          <ProjectIconPicker value={appearance} projectKey={d.project.key} onChange={setAppearance} disabled={!isAdmin || patch.isPending} />
          <button type="button" className="btn h-8" disabled={!isAdmin || patch.isPending || (appearance.icon === (d.project.icon ?? null) && appearance.color === (d.project.color ?? DEFAULT_PROJECT_COLOR))} onClick={() => patch.mutate(appearance)}>{t("common.save")}</button>
        </div>
      </div>
      <Field label={t("project.visibility")}>
        <select className="input h-8 max-w-[200px]" value={d.project.visibility} disabled={!isAdmin} onChange={(e) => patch.mutate({ visibility: e.target.value })}>
          <option value="public">{t("project.public")}</option>
          <option value="private">{t("project.private")}</option>
        </select>
      </Field>
      <label className="flex items-center gap-2 text-[13px]">
        <input type="checkbox" checked={d.project.estimate_enabled} disabled={!isAdmin} onChange={(e) => patch.mutate({ estimate_enabled: e.target.checked })} /> {t("projectSettings.estimates")}
      </label>
      {isAdmin && (
        <div className="mt-4 rounded-lg border border-danger/40 p-4">
          <h3 className="text-[13px] font-semibold text-danger">{t("projectSettings.dangerZone")}</h3>
          <p className="mt-1 text-[12.5px] text-muted">{t("projectSettings.deleteHint", { key: d.project.key })}</p>
          <div className="mt-2 flex gap-2">
            <input className="input h-8 w-[160px] font-mono" placeholder={d.project.key} value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} />
            <button className="btn h-8 border-danger/60 text-danger" disabled={confirm !== d.project.key || del.isPending} onClick={() => del.mutate()}>{t("projectSettings.deleteProject")}</button>
          </div>
        </div>
      )}
    </section>
  );
}

function States({ d, isAdmin }: { d: ProjectData; isAdmin: boolean }) {
  const { t } = useTranslation();
  const states = liveStates(d);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<string>("backlog");
  const apply = (s: (typeof states)[number]) => useProjectStore.getState().applyState(s);
  const create = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/workspaces/{slug}/projects/{key}/states", { params: { path: { slug: d.slug, key: d.project.key } }, body: { name: newName.trim(), type: newType as never } })),
    onSuccess: (s) => { apply(s); setNewName(""); },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const update = useMutation({
    mutationFn: async (v: { id: string; body: Record<string, unknown> }) => unwrap(await api.PATCH("/api/states/{id}", { params: { path: { id: v.id } }, body: v.body })),
    onSuccess: (s) => { apply(s); if (s.is_default) void useProjectStore.getState().refreshDelta(d.project.id); },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const remove = useMutation({
    mutationFn: async (v: { id: string; replacement: string }) => unwrap(await api.DELETE("/api/states/{id}", { params: { path: { id: v.id }, query: { replacement: v.replacement } } })),
    onSuccess: (s) => { apply(s); void useProjectStore.getState().refreshDelta(d.project.id); },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  return (
    <section className="flex flex-col gap-5">
      <h2 className="text-[16px] font-semibold">{t("projectSettings.states")}</h2>
      <p className="text-[12.5px] text-muted">{t("projectSettings.statesHint")}</p>
      {STATE_TYPE_ORDER.map((type) => (
        <div key={type}>
          <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-faint">{t(`stateType.${type}`)}</div>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {states.filter((s) => s.type === type).map((s) => (
              <li key={s.id} className="flex items-center gap-2 px-3 py-1.5">
                <input type="color" value={s.color} disabled={!isAdmin} className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0" onChange={(e) => update.mutate({ id: s.id, body: { color: e.target.value } })} />
                <StateIcon state={s} />
                <input className="h-7 flex-1 rounded bg-transparent px-1 text-[13px] hover:bg-hover focus:bg-bg" defaultValue={s.name} disabled={!isAdmin} onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== s.name && update.mutate({ id: s.id, body: { name: e.target.value.trim() } })} />
                {s.is_default ? <span className="text-[11px] text-faint">{t("projectSettings.default")}</span> : isAdmin && type === "unstarted" || type === "backlog" ? <button className="text-[11px] text-muted hover:text-fg" onClick={() => update.mutate({ id: s.id, body: { is_default: true } })}>{t("projectSettings.makeDefault")}</button> : null}
                {isAdmin && states.filter((x) => x.type === type).length > 1 && (
                  <button className="text-muted hover:text-danger" title={t("common.delete")} onClick={() => {
                    const replacement = states.find((x) => x.type === type && x.id !== s.id)!;
                    remove.mutate({ id: s.id, replacement: replacement.id });
                  }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {isAdmin && (
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <input className="input h-8 w-[220px]" placeholder={t("projectSettings.newState")} value={newName} onChange={(e) => setNewName(e.target.value)} />
          <select className="input h-8 w-[140px]" value={newType} onChange={(e) => setNewType(e.target.value)}>
            {STATE_TYPE_ORDER.map((tp) => <option key={tp} value={tp}>{t(`stateType.${tp}`)}</option>)}
          </select>
          <button className="btn h-8" type="submit" disabled={!newName.trim()}><Plus className="h-3.5 w-3.5" /> {t("common.create")}</button>
        </form>
      )}
    </section>
  );
}

function Labels({ d, canEdit }: { d: ProjectData; canEdit: boolean }) {
  const { t } = useTranslation();
  const labels = liveLabels(d);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#5e6ad2");
  const apply = (l: (typeof labels)[number]) => useProjectStore.getState().applyLabel(l);
  const create = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/workspaces/{slug}/projects/{key}/labels", { params: { path: { slug: d.slug, key: d.project.key } }, body: { name: name.trim(), color } })),
    onSuccess: (l) => { apply(l); setName(""); },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const update = useMutation({
    mutationFn: async (v: { id: string; body: Record<string, unknown> }) => unwrap(await api.PATCH("/api/labels/{id}", { params: { path: { id: v.id } }, body: v.body })),
    onSuccess: apply,
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const remove = useMutation({
    mutationFn: async (id: string) => unwrap(await api.DELETE("/api/labels/{id}", { params: { path: { id } } })),
    onSuccess: (l) => { apply(l); void useProjectStore.getState().refreshDelta(d.project.id); },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  return (
    <section className="flex flex-col gap-5">
      <h2 className="text-[16px] font-semibold">{t("projectSettings.labels")}</h2>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {labels.map((l) => (
          <li key={l.id} className="flex items-center gap-2 px-3 py-1.5">
            <input type="color" value={l.color} disabled={!canEdit} className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0" onChange={(e) => update.mutate({ id: l.id, body: { color: e.target.value } })} />
            <LabelDot color={l.color} />
            <input className="h-7 flex-1 rounded bg-transparent px-1 text-[13px] hover:bg-hover focus:bg-bg" defaultValue={l.name} disabled={!canEdit} onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== l.name && update.mutate({ id: l.id, body: { name: e.target.value.trim() } })} />
            {canEdit && <button className="text-muted hover:text-danger" onClick={() => remove.mutate(l.id)}><Trash2 className="h-3.5 w-3.5" /></button>}
          </li>
        ))}
        {labels.length === 0 && <li className="px-3 py-3 text-[12.5px] text-faint">{t("projectSettings.noLabels")}</li>}
      </ul>
      {canEdit && (
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-8 w-8 cursor-pointer rounded border border-border-strong bg-transparent" />
          <input className="input h-8 w-[220px]" placeholder={t("projectSettings.newLabel")} value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn h-8" type="submit" disabled={!name.trim()}><Plus className="h-3.5 w-3.5" /> {t("common.create")}</button>
        </form>
      )}
    </section>
  );
}

function TrashSection({ d }: { d: ProjectData }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["trash", d.slug, d.project.key],
    queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/projects/{key}/trash", { params: { path: { slug: d.slug, key: d.project.key } } })),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["trash", d.slug, d.project.key] });
    void qc.invalidateQueries({ queryKey: ["docs", d.slug, d.project.key] });
    void useProjectStore.getState().refreshDelta(d.project.id);
  };
  const restoreTask = useMutation({ mutationFn: async (id: string) => unwrap(await api.POST("/api/tasks/{id}/restore", { params: { path: { id } } })), onSuccess: refresh, onError: (e) => toast(errorMessage(e), { tone: "error" }) });
  const restoreDoc = useMutation({ mutationFn: async (id: string) => unwrap(await api.POST("/api/docs/{id}/restore", { params: { path: { id } } })), onSuccess: refresh, onError: (e) => toast(errorMessage(e), { tone: "error" }) });
  const total = (q.data?.issues.length ?? 0) + (q.data?.tasks.length ?? 0) + (q.data?.docs.length ?? 0);
  const Row = ({ label, when, onRestore }: { label: React.ReactNode; when: string | null | undefined; onRestore: () => void }) => (
    <li className="flex items-center gap-3 px-3 py-2 text-[13px]">
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="text-[11.5px] text-faint">{when ? formatRelative(when, i18n.language) : ""}</span>
      <button className="btn h-7 text-[12px]" onClick={onRestore}>{t("trash.restore")}</button>
    </li>
  );
  return (
    <section className="flex flex-col gap-5">
      <h2 className="text-[16px] font-semibold">{t("trash.title")}</h2>
      <p className="text-[12.5px] text-muted">{t("trash.hint")}</p>
      {q.isPending ? <p className="text-[12.5px] text-muted">{t("common.loading")}</p> : total === 0 ? <p className="text-[12.5px] text-faint">{t("trash.empty")}</p> : (
        <>
          {q.data!.issues.length > 0 && <div><h3 className="mb-1 text-[12px] font-medium text-muted">{t("nav.issues")}</h3><ul className="divide-y divide-border rounded-lg border border-border">{q.data!.issues.map((i) => <Row key={i.id} label={<><span className="font-mono text-[11.5px] text-muted">{d.project.key}-{i.number}</span> {i.title}</>} when={i.deleted_at} onRestore={() => void restoreIssue(i).then(refresh).catch((e) => toast(errorMessage(e), { tone: "error" }))} />)}</ul></div>}
          {q.data!.tasks.length > 0 && <div><h3 className="mb-1 text-[12px] font-medium text-muted">{t("nav.tasks")}</h3><ul className="divide-y divide-border rounded-lg border border-border">{q.data!.tasks.map((x) => <Row key={x.id} label={x.name} when={x.deleted_at} onRestore={() => restoreTask.mutate(x.id)} />)}</ul></div>}
          {q.data!.docs.length > 0 && <div><h3 className="mb-1 text-[12px] font-medium text-muted">{t("nav.docs")}</h3><ul className="divide-y divide-border rounded-lg border border-border">{q.data!.docs.map((x) => <Row key={x.id} label={x.title} when={x.deleted_at} onRestore={() => restoreDoc.mutate(x.id)} />)}</ul></div>}
        </>
      )}
    </section>
  );
}
